import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';
import './RegressionRadar.css';

const TABS = [
  { id: 'optimal', icon: '⚡', labelKey: 'regressionRadar.tabOptimal' },
  { id: 'scope', icon: '🔭', labelKey: 'regressionRadar.tabScope' },
  { id: 'risk', icon: '🎯', labelKey: 'regressionRadar.tabRisk' }
];

// ── How It Works — collapsible explainer per tab ─────────────────────────────
function HowItWorks({ tabId, t }) {
  const [open, setOpen] = useState(false);

  const content = {
    optimal: {
      title: t('regressionRadar.howItWorks.optimal.title', 'How does it work?'),
      why: t('regressionRadar.howItWorks.optimal.why', 'You have lots of tests, but which ones to run first? This feature finds the smallest set of tests that covers the maximum number of nodes in your graph — saving time without losing coverage.'),
      steps: [
        t('regressionRadar.howItWorks.optimal.step1', 'We scan every node and its associated tests'),
        t('regressionRadar.howItWorks.optimal.step2', 'Each test "covers" the node it belongs to + its direct neighbours'),
        t('regressionRadar.howItWorks.optimal.step3', 'A greedy set-cover algorithm picks tests one by one, always choosing the one that covers the most uncovered nodes'),
        t('regressionRadar.howItWorks.optimal.step4', 'Result: the minimum tests to run for maximum coverage')
      ],
      tip: t('regressionRadar.howItWorks.optimal.tip', 'Add tests to your nodes to improve coverage. Nodes without tests appear in the warning section below.')
    },
    scope: {
      title: t('regressionRadar.howItWorks.scope.title', 'How does it work?'),
      why: t('regressionRadar.howItWorks.scope.why', '"If I modify this node, what else could break?" This tab answers that question by traversing your graph from any node outward.'),
      steps: [
        t('regressionRadar.howItWorks.scope.step1', 'Select a node you plan to change'),
        t('regressionRadar.howItWorks.scope.step2', 'Choose the depth (1-6 hops) — how far to look for dependencies'),
        t('regressionRadar.howItWorks.scope.step3', 'BFS traversal finds all connected nodes, sorted by distance'),
        t('regressionRadar.howItWorks.scope.step4', 'Each impacted node shows its test status — red = no tests, green = covered')
      ],
      tip: t('regressionRadar.howItWorks.scope.tip', 'Start with 2-3 hops. If nothing shows up, increase depth.')
    },
    risk: {
      title: t('regressionRadar.howItWorks.risk.title', 'How does it work?'),
      why: t('regressionRadar.howItWorks.risk.why', 'Not all nodes carry the same regression risk. This tab ranks every node by combining 3 factors to surface the most dangerous ones.'),
      steps: [
        t('regressionRadar.howItWorks.risk.step1', 'Connectivity (C) — how many connections does the node have? More connections = more risk'),
        t('regressionRadar.howItWorks.risk.step2', 'Dependents (D) — how many other nodes depend on it? High in-degree = more impact'),
        t('regressionRadar.howItWorks.risk.step3', 'Test Gap (G) — does it have tests? No tests = blind spot'),
        t('regressionRadar.howItWorks.risk.step4', 'Final score = weighted sum × criticity multiplier (high/critical nodes score higher)')
      ],
      tip: t('regressionRadar.howItWorks.risk.tip', 'Focus on critical (red) nodes first. Add tests there for the biggest impact on your health score.')
    }
  };

  const c = content[tabId];
  if (!c) return null;

  return (
    <div className={`radar-how-it-works radar-how-it-works--${tabId}`}>
      <button
        className="radar-how-it-works__toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="radar-how-it-works__toggle-icon">{open ? '▾' : '▸'}</span>
        <span className="radar-how-it-works__toggle-label">💡 {c.title}</span>
      </button>
      {open && (
        <div className="radar-how-it-works__content">
          <p className="radar-how-it-works__why">{c.why}</p>
          <ol className="radar-how-it-works__steps">
            {c.steps.map((step, i) => (
              <li key={i} className="radar-how-it-works__step">{step}</li>
            ))}
          </ol>
          <div className="radar-how-it-works__tip">
            <span className="radar-how-it-works__tip-icon">💬</span>
            <span className="radar-how-it-works__tip-text">{c.tip}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function RegressionRadar() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('optimal');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Risk state
  const [riskData, setRiskData] = useState(null);

  // Scope state
  const [scopeData, setScopeData] = useState(null);
  const [scopeNodeName, setScopeNodeName] = useState('');
  const [scopeDepth, setScopeDepth] = useState(3);
  const [nodeNames, setNodeNames] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);

  // Optimal state
  const [optimalData, setOptimalData] = useState(null);

  // Load node names for autocomplete on mount
  useEffect(() => {
    apiClient.get('/api/regression-radar/node-names')
      .then(res => { if (res.data?.success) setNodeNames(res.data.data); })
      .catch(() => {});
  }, []);

  // Load optimal data on mount (primary feature)
  useEffect(() => {
    loadOptimalData();
  }, []);

  const loadRiskData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/regression-radar/risk-scores');
      if (res.data?.success) setRiskData(res.data.data);
      else setError(res.data?.error || 'Unknown error');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOptimalData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/regression-radar/optimal-tests');
      if (res.data?.success) setOptimalData(res.data.data);
      else setError(res.data?.error || 'Unknown error');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadScopeData = useCallback(async () => {
    if (!scopeNodeName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/api/regression-radar/scope/${encodeURIComponent(scopeNodeName.trim())}?depth=${scopeDepth}`);
      if (res.data?.success) setScopeData(res.data.data);
      else setError(res.data?.error || 'Unknown error');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [scopeNodeName, scopeDepth]);

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    setError(null);
    if (tabId === 'risk' && !riskData) loadRiskData();
    if (tabId === 'optimal' && !optimalData) loadOptimalData();
  }, [riskData, optimalData, loadRiskData, loadOptimalData]);

  const filteredNames = useMemo(() => {
    if (!scopeNodeName.trim()) return nodeNames.slice(0, 10);
    const lower = scopeNodeName.toLowerCase();
    return nodeNames.filter(n => n.toLowerCase().includes(lower)).slice(0, 10);
  }, [scopeNodeName, nodeNames]);

  // ── Render: Risk Scores Tab ────────────────────────────────────────────────
  const renderRiskTab = () => {
    if (!riskData) return null;
    const { nodes, summary } = riskData;
    return (
      <div className="radar-tab-content radar-tab-content--risk">
        <HowItWorks tabId="risk" t={t} />
        <div className="radar-dashboard radar-dashboard--risk">
          <div className="radar-stat-card info">
            <div className="stat-icon">📊</div>
            <div className="stat-value">{summary.totalNodes}</div>
            <div className="stat-label">{t('regressionRadar.totalNodes', 'Total Nodes')}</div>
          </div>
          <div className="radar-stat-card critical">
            <div className="stat-icon">🔴</div>
            <div className="stat-value">{summary.criticalCount}</div>
            <div className="stat-label">{t('regressionRadar.criticalNodes', 'Critical')}</div>
          </div>
          <div className="radar-stat-card warning">
            <div className="stat-icon">🟠</div>
            <div className="stat-value">{summary.highCount}</div>
            <div className="stat-label">{t('regressionRadar.highRisk', 'High Risk')}</div>
          </div>
          <div className="radar-stat-card success">
            <div className="stat-icon">💚</div>
            <div className="stat-value">{summary.healthScore}%</div>
            <div className="stat-label">{t('regressionRadar.healthScore', 'Health Score')}</div>
          </div>
        </div>

        <div className="radar-section radar-section--risk-ranking">
          <h3 className="radar-section__title radar-section__title--risk">🎯 {t('regressionRadar.riskRanking', 'Risk Ranking')}</h3>
          {nodes.length === 0 ? (
            <div className="radar-empty">
              <div className="empty-icon">📭</div>
              <p>{t('regressionRadar.noNodes', 'No nodes in the graph yet.')}</p>
            </div>
          ) : (
            <table className="risk-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('regressionRadar.colNode', 'Node')}</th>
                  <th>{t('regressionRadar.colType', 'Type')}</th>
                  <th>{t('regressionRadar.colRisk', 'Risk')}</th>
                  <th>{t('regressionRadar.colConnections', 'Connections')}</th>
                  <th>{t('regressionRadar.colTests', 'Tests')}</th>
                  <th>{t('regressionRadar.colFactors', 'Factors')}</th>
                </tr>
              </thead>
              <tbody>
                {nodes.slice(0, 25).map((node, i) => (
                  <tr key={node.name}>
                    <td style={{ fontWeight: 700, color: '#9ca3af' }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{node.name}</td>
                    <td><span className="risk-badge medium">{node.type}</span></td>
                    <td>
                      <div className="risk-bar-container">
                        <div className="risk-bar">
                          <div
                            className={`risk-bar-fill ${node.riskLevel}`}
                            style={{ width: `${node.riskScore}%` }}
                          />
                        </div>
                        <span className="risk-bar-value">{node.riskScore}</span>
                      </div>
                    </td>
                    <td>{node.degree} <span style={{ color: '#9ca3af', fontSize: 11 }}>({node.inDegree} in)</span></td>
                    <td>
                      <span className={`risk-badge ${node.testCount > 0 ? 'low' : 'critical'}`}>
                        {node.testCount > 0 ? `${node.testCount} ✓` : '0 ✗'}
                      </span>
                    </td>
                    <td style={{ fontSize: 11, color: '#6b7280' }}>
                      C:{node.factors.connectivity}% D:{node.factors.dependents}% G:{node.factors.testGap}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  // ── Render: Regression Scope Tab ───────────────────────────────────────────
  const renderScopeTab = () => (
    <div className="radar-tab-content radar-tab-content--scope">
      <HowItWorks tabId="scope" t={t} />
      <div className="radar-section radar-section--scope-search">
        <h3 className="radar-section__title radar-section__title--scope">🔭 {t('regressionRadar.scopeTitle', 'Regression Scope Analysis')}</h3>
        <div className="scope-search">
          <div className="scope-search-input">
            <input
              type="text"
              value={scopeNodeName}
              onChange={(e) => {
                setScopeNodeName(e.target.value);
                setShowAutocomplete(true);
              }}
              onFocus={() => setShowAutocomplete(true)}
              onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
              placeholder={t('regressionRadar.searchNode', 'Search a node to analyze...')}
            />
            {showAutocomplete && filteredNames.length > 0 && (
              <div className="scope-autocomplete">
                {filteredNames.map(name => (
                  <div
                    key={name}
                    className="scope-autocomplete-item"
                    onMouseDown={() => {
                      setScopeNodeName(name);
                      setShowAutocomplete(false);
                    }}
                  >
                    {name}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="scope-depth-select">
            <label>{t('regressionRadar.depth', 'Depth')}</label>
            <select value={scopeDepth} onChange={e => setScopeDepth(parseInt(e.target.value))}>
              {[1,2,3,4,5,6].map(d => (
                <option key={d} value={d}>{d} {d === 1 ? 'hop' : 'hops'}</option>
              ))}
            </select>
          </div>
          <button
            className="scope-analyze-btn"
            onClick={loadScopeData}
            disabled={!scopeNodeName.trim() || loading}
          >
            {t('regressionRadar.analyze', 'Analyze')}
          </button>
        </div>
      </div>

      {scopeData && (
        <>
          <div className="radar-dashboard radar-dashboard--scope">
            <div className="radar-stat-card info">
              <div className="stat-icon">📡</div>
              <div className="stat-value">{scopeData.summary.totalImpacted}</div>
              <div className="stat-label">{t('regressionRadar.impacted', 'Impacted')}</div>
            </div>
            <div className="radar-stat-card critical">
              <div className="stat-icon">⚠️</div>
              <div className="stat-value">{scopeData.summary.untestedCount}</div>
              <div className="stat-label">{t('regressionRadar.untested', 'Untested')}</div>
            </div>
            <div className="radar-stat-card success">
              <div className="stat-icon">🛡️</div>
              <div className="stat-value">{scopeData.summary.coveragePercent}%</div>
              <div className="stat-label">{t('regressionRadar.coverage', 'Coverage')}</div>
            </div>
          </div>

          <div className="radar-section radar-section--scope-results">
            <h3 className="radar-section__title radar-section__title--scope-results">📍 {t('regressionRadar.impactedNodes', 'Impacted Nodes')} — {scopeData.origin}</h3>
            <div className="scope-legend">
              <div className="scope-legend-item"><div className="scope-legend-dot d1" /><span>1 hop</span></div>
              <div className="scope-legend-item"><div className="scope-legend-dot d2" /><span>2 hops</span></div>
              <div className="scope-legend-item"><div className="scope-legend-dot d3" /><span>3 hops</span></div>
              <div className="scope-legend-item"><div className="scope-legend-dot d4" /><span>4+ hops</span></div>
            </div>
            {scopeData.impactedNodes.length === 0 ? (
              <div className="radar-empty">
                <div className="empty-icon">🏝️</div>
                <p>{t('regressionRadar.noImpact', 'This node has no dependencies — no regression risk!')}</p>
              </div>
            ) : (
              <div className="impact-nodes-grid">
                {scopeData.impactedNodes.map(node => (
                  <div key={node.name} className={`impact-node-card distance-${Math.min(node.distance, 4)}`}>
                    <div className="node-name">{node.name}</div>
                    <div className="node-meta">
                      <span>{node.type}</span>
                      <span>{t('regressionRadar.distanceLabel', 'Distance')}: {node.distance}</span>
                      <span className={`risk-badge ${node.hasTests ? 'low' : 'critical'}`}>
                        {node.hasTests ? `${node.testCount} tests` : t('regressionRadar.noTests', 'No tests')}
                      </span>
                    </div>
                    {node.testNames.length > 0 && (
                      <div className="node-tests">
                        {node.testNames.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  // ── Render: Optimal Test Set Tab ───────────────────────────────────────────
  const renderOptimalTab = () => {
    if (!optimalData) return null;
    const { optimalTests, summary, uncoveredNodes, nodesWithoutTests } = optimalData;
    return (
      <div className="radar-tab-content radar-tab-content--optimal">
        <HowItWorks tabId="optimal" t={t} />
        <div className="radar-dashboard radar-dashboard--optimal">
          <div className="radar-stat-card info">
            <div className="stat-icon">🧪</div>
            <div className="stat-value">{summary.totalTestsInGraph}</div>
            <div className="stat-label">{t('regressionRadar.totalTests', 'Total Tests')}</div>
          </div>
          <div className="radar-stat-card success">
            <div className="stat-icon">⚡</div>
            <div className="stat-value">{summary.optimalTestCount}</div>
            <div className="stat-label">{t('regressionRadar.optimalCount', 'Optimal Set')}</div>
          </div>
          <div className="radar-stat-card warning">
            <div className="stat-icon">📉</div>
            <div className="stat-value">{summary.reductionPercent}%</div>
            <div className="stat-label">{t('regressionRadar.reduction', 'Reduction')}</div>
          </div>
          <div className="radar-stat-card critical">
            <div className="stat-icon">🕳️</div>
            <div className="stat-value">{summary.nodesWithoutTestsCount}</div>
            <div className="stat-label">{t('regressionRadar.uncovered', 'Uncovered Nodes')}</div>
          </div>
        </div>

        <div className="radar-section radar-section--optimal-list">
          <h3 className="radar-section__title radar-section__title--optimal">⚡ {t('regressionRadar.optimalSet', 'Optimal Test Set')}</h3>
          {optimalTests.length === 0 ? (
            <div className="radar-empty">
              <div className="empty-icon">🧪</div>
              <p>{t('regressionRadar.noTestsYet', 'No tests in the graph yet. Add tests to your nodes!')}</p>
            </div>
          ) : (
            <div className="optimal-test-list">
              {optimalTests.map((test, i) => (
                <div key={test.testName} className="optimal-test-item">
                  <div className="optimal-test-rank">{i + 1}</div>
                  <div className="optimal-test-info">
                    <div className="test-name">{test.testName}</div>
                    <div className="test-coverage">
                      {t('regressionRadar.covers', 'Covers')} {test.coversCount} {t('regressionRadar.nodesLabel', 'nodes')}
                    </div>
                    <div className="test-nodes">
                      {test.coversNodes.slice(0, 8).map(n => (
                        <span key={n} className="node-chip">{n}</span>
                      ))}
                      {test.coversNodes.length > 8 && (
                        <span className="node-chip">+{test.coversNodes.length - 8}</span>
                      )}
                    </div>
                  </div>
                  <div className="optimal-test-covers-count">
                    {test.coversCount} ✓
                  </div>
                </div>
              ))}
            </div>
          )}

          {nodesWithoutTests.length > 0 && (
            <div className="uncovered-warning">
              <h4>⚠️ {t('regressionRadar.noTestNodes', 'Nodes without any tests')} ({nodesWithoutTests.length})</h4>
              <div className="uncovered-nodes-list">
                {nodesWithoutTests.slice(0, 20).map(n => (
                  <span key={n} className="uncovered-node-chip">{n}</span>
                ))}
                {nodesWithoutTests.length > 20 && (
                  <span className="uncovered-node-chip">+{nodesWithoutTests.length - 20}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Main Render ────────────────────────────────────────────────────────────
  return (
    <div className="regression-radar">
      <p className="radar-subtitle">{t('regressionRadar.subtitle', 'Find the minimum tests to run for maximum regression coverage — powered by your graph')}</p>

      <div className="radar-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`radar-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            {t(tab.labelKey, tab.id === 'optimal' ? 'Optimal Tests' : tab.id === 'scope' ? 'What If?' : 'Risk Map')}
          </button>
        ))}
      </div>

      {error && (
        <div className="radar-error">
          ❌ {error}
        </div>
      )}

      {loading ? (
        <div className="radar-loading">
          <div className="radar-spinner" />
          <p>{t('regressionRadar.analyzing', 'Analyzing your graph...')}</p>
        </div>
      ) : (
        <>
          {activeTab === 'risk' && renderRiskTab()}
          {activeTab === 'scope' && renderScopeTab()}
          {activeTab === 'optimal' && renderOptimalTab()}
        </>
      )}
    </div>
  );
}
